"use client";
import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import { ArrowBack, Print } from "@mui/icons-material";

const backendUrl = "https://erp.tsc.go.ke/tpay";

const formatNumber = (num: number | string) => {
  const n = typeof num === "string" ? parseFloat(num) : num;
  if (isNaN(n)) return "0.00";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface ResultItem {
  PayDate: string;
  TaxablePay: string | number;
  PFNDauto: string | number;
  PFNDmanual: string | number;
  NSSFauto: string | number;
  NSSFmanual: string | number;
  PAYEauto: string | number;
  NHIFauto: string | number;
  AHLauto: string | number;
  UnusedMPR: string | number;
  MPRvalue: string | number;
  Arrears: string | number;
  PAYEmanual: string | number;
  VoteCode: string;
}

interface Summary {
  VoteCode?: string;
  TSC_PIN?: string;
  Name?: string;
  TSC_NO?: string;
  ID_NO?: string;
  KRA_PIN?: string;
}

const Dashboard: React.FC = () => {
  const [payroll_no, setPayrollNo] = useState("");
  const [id_no, setIdNo] = useState("");
  const [surname, setSurname] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState("");

  const [validationErrors, setValidationErrors] = useState({
    payroll_no: "",
    id_no: "",
    surname: "",
  });

  const [touched, setTouched] = useState({
    payroll_no: false,
    id_no: false,
    surname: false,
  });

  const [totals, setTotals] = useState({
    TaxablePay: 0,
    Pension: 0,
    PAYEauto: 0,
    UnusedMPR: 0,
    MPRvalue: 0,
    Arrears: 0,
    PAYEmanual: 0,
    NHIFauto: 0,
    AHLauto: 0,
  });

  const handleBlur = (field: keyof typeof touched) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  const validateField = (field: keyof typeof validationErrors) => {
    let isValid = true;
    let errorMessage = "";

    switch (field) {
      case "payroll_no":
        isValid = payroll_no.trim().length >= 3;
        errorMessage = isValid
          ? ""
          : "Payroll number must be at least 3 characters";
        break;
      case "id_no":
        isValid = /^[0-9]{7,8}$/.test(id_no);
        errorMessage = isValid
          ? ""
          : "Please enter a valid Kenyan ID number (7 or 8 digits)";
        break;
      case "surname":
        isValid = surname.trim().length >= 2;
        errorMessage = isValid ? "" : "Surname must be at least 2 characters";
        break;
    }

    setValidationErrors((prev) => ({ ...prev, [field]: errorMessage }));
    return isValid;
  };

  const validateForm = () =>
    validateField("payroll_no") &&
    validateField("id_no") &&
    validateField("surname");

 const handleSubmit = async () => {
  setTouched({ payroll_no: true, id_no: true, surname: true });

  if (!validateForm()) return;

  setLoading(true);
  setError("");
  setResults([]);
  setSummary(null);
  setTotals({
    TaxablePay: 0,
    Pension: 0,
    PAYEauto: 0,
    UnusedMPR: 0,
    MPRvalue: 0,
    Arrears: 0,
    PAYEmanual: 0,
    NHIFauto: 0,
    AHLauto: 0,
  });

  try {
    const res = await fetch(`${backendUrl}/p9/retiries-eoy-totals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payroll_no, id_no, surname }),
    });

    if (!res.ok) throw new Error("Failed to fetch data");

    const data = await res.json();
    const results: ResultItem[] = (data.results || []).slice();

    // ===== Sort results by month (ignoring day/year) =====
    results.sort((a, b) => {
      const getMonth = (dateStr: string) => {
        const parts = dateStr.split("/").map(Number); // [day, month, year]
        return parts[1]; // month (1-12)
      };
      return getMonth(a.PayDate) - getMonth(b.PayDate);
    });

    setResults(results);
    setSummary(data.summary || null);

    // ===== Calculate totals =====
    const totals = results.reduce(
      (acc, item) => {
        // const parseNum = (val: string | number) =>
        //   typeof val === "number" ? val : parseFloat(val) || 0;
        const parseNum = (val: any) => Number(val ?? 0) || 0;

        acc.TaxablePay += parseNum(item.TaxablePay);
        acc.Pension +=
          parseNum(item.PFNDauto) +
          parseNum(item.NSSFauto) +
          parseNum(item.PFNDmanual) +
          parseNum(item.NSSFauto) +
          parseNum(item.NSSFmanual);
        acc.PAYEauto += parseNum(item.PAYEauto);
        acc.UnusedMPR += parseNum(item.UnusedMPR);
        acc.MPRvalue += parseNum(item.MPRvalue);
        acc.Arrears += parseNum(item.Arrears);
        acc.PAYEmanual += parseNum(item.PAYEmanual);
        acc.NHIFauto += parseNum(item.NHIFauto);
        acc.AHLauto += parseNum(item.AHLauto);

        return acc;
      },
      {
        TaxablePay: 0,
        Pension: 0,
        PAYEauto: 0,
        UnusedMPR: 0,
        MPRvalue: 0,
        Arrears: 0,
        PAYEmanual: 0,
        NHIFauto: 0,
        AHLauto: 0,
      }
    );

    setTotals(totals);
  } catch (err: unknown) {
    console.error("Error fetching payroll data:", err);
    setError("You are not a former employee. Try again!!");
  } finally {
    setLoading(false);
  }
};



  const handleReset = () => {
    setResults([]);
    setSummary(null);
    setPayrollNo("");
    setIdNo("");
    setSurname("");
    setValidationErrors({ payroll_no: "", id_no: "", surname: "" });
    setTouched({ payroll_no: false, id_no: false, surname: false });
    setTotals({
      TaxablePay: 0,
      Pension: 0,
      PAYEauto: 0,
      UnusedMPR: 0,
      MPRvalue: 0,
      Arrears: 0,
      PAYEmanual: 0,
      NHIFauto: 0,
      AHLauto: 0,
    });
    setError("");
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {!results.length ? (
        <Box
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 2,
            boxShadow: "0px 3px 10px rgba(0, 0, 0, 0.08)",
            background: "linear-gradient(to bottom, #f9fafb, #ffffff)",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            color="#0B3D91"
            align="center"
            gutterBottom
          >
          2025 P9 Tax Summary Request for Former Employees
          </Typography>
           <Divider sx={{ borderColor: "#133154", borderWidth: 1, mb: 3 }} />

          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 3,
            }}
          >
            <TextField
              label="Payroll Number *"
              value={payroll_no}
              onChange={(e) => setPayrollNo(e.target.value)}
              onBlur={() => handleBlur("payroll_no")}
              disabled={loading}
              error={touched.payroll_no && Boolean(validationErrors.payroll_no)}
              helperText={touched.payroll_no && validationErrors.payroll_no}
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              label="ID Number *"
              value={id_no}
              onChange={(e) => setIdNo(e.target.value)}
              onBlur={() => handleBlur("id_no")}
              disabled={loading}
              error={touched.id_no && Boolean(validationErrors.id_no)}
              helperText={touched.id_no && validationErrors.id_no}
              placeholder="e.g. 12345678"
              inputProps={{ maxLength: 8, pattern: "[0-9]*" }}
            />
            <TextField
              label="Surname *"
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              onBlur={() => handleBlur("surname")}
              disabled={loading}
              error={touched.surname && Boolean(validationErrors.surname)}
              helperText={touched.surname && validationErrors.surname}
              inputProps={{ maxLength: 50 }}
            />
          </Box>

          <Box textAlign="center" mt={4}>
              <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
              minWidth: 200,
              backgroundColor: '#133154',
              '&:hover': {
              backgroundColor: '#133153', // slightly darker for hover
              },
              }}
              >
              {loading ? (
              <CircularProgress size={24} color="inherit" />
              ) : (
              "Fetch Summary"
              )}
              </Button>

          </Box>

          {error && (
            <Typography color="error" mt={2} align="center" fontWeight="medium">
              {error}
            </Typography>
          )}
        </Box>
      ) : (
        <Box className="print-section">
          {/* Report Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 2,
              backgroundColor: "#f5f7fa",
            }}
          >
            {/* Left Logo */}
            <Box
              component="img"
              src="https://tpay.tsc.go.ke/tp/v1/images/backg44.png"
              alt="TSC Logo"
              sx={{ height: 84 }}
            />

            {/* Centered Header Section */}
            <Box sx={{ textAlign: "center", flexGrow: 1, px: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="#133154">
                GOVERNMENT OF KENYA - TEACHERS SERVICE COMMISSION
              </Typography>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                IPPD - End of Year Tax Returns (P9-FORM)
              </Typography>

              <Typography>Employer Vote: {summary?.VoteCode || "-"}</Typography>
              <Typography>
                Employer Tax-PIN: {summary?.TSC_PIN || "-"} &nbsp;&nbsp; Tax-Year: {new Date().getFullYear() - 1}
              </Typography>
              <Typography>Tax Payer's Name: {summary?.Name || "-"}</Typography>
              <Typography>
                TSC No: {summary?.TSC_NO || "-"} &nbsp;&nbsp; ID No: {summary?.ID_NO || "-"} &nbsp;&nbsp; PIN:{" "}
                {summary?.KRA_PIN || "-"}
              </Typography>
            </Box>

            {/* Right Logo */}
            <Box
              component="img"
              src="https://tpay.tsc.go.ke/tp/v1/images/ippdlogo.jpg"
              alt="IPPD Logo"
              sx={{ height: 84 }}
            />
          </Box>

          <Divider sx={{ borderColor: "#133154", borderWidth: 1, mb: 3 }} />

          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small" stickyHeader>
              <TableHead sx={{ backgroundColor: "#133154" }}>
                <TableRow>
                  <TableCell sx={{ color: "black" }}>PAY DATE</TableCell>
                  <TableCell sx={{ color: "black" }} align="right">
                    Taxable Pay (KES)
                  </TableCell>
                  <TableCell sx={{ color: "black" }} align="right">
                    Pension (KES)
                  </TableCell>
                  <TableCell sx={{ color: "black" }} align="right">
                    PAYE auto (KES)
                  </TableCell>
                <TableCell sx={{ color: "black" }} align="right">
                  SHA Auto (KES)
                </TableCell>
                <TableCell sx={{ color: "black" }} align="right">
                  AHL Auto (KES)
                </TableCell>
                  <TableCell sx={{ color: "black" }} align="right">
                    Unused MPR (KES)
                  </TableCell>
                  <TableCell sx={{ color: "black" }} align="right">
                    MPR value (KES)
                  </TableCell>
                  <TableCell sx={{ color: "black" }} align="right">
                    Arrears (KES)
                  </TableCell>
                  <TableCell sx={{ color: "black" }} align="right">
                    PAYE manual (KES)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.PayDate}</TableCell>
                    <TableCell align="right">
                      {formatNumber(row.TaxablePay)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(
                        (parseFloat(row.PFNDauto as string) || 0) +
                          (parseFloat(row.PFNDmanual as string) || 0) +
                          (parseFloat(row.NSSFauto as string) || 0) +
                           (parseFloat(row.NSSFauto as string) || 0) +
                          (parseFloat(row.NSSFmanual as string) || 0)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.PAYEauto)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.NHIFauto)}
                    </TableCell>

                    <TableCell align="right">
                      {formatNumber(row.AHLauto)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.UnusedMPR)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.MPRvalue)}
                    </TableCell>
                    <TableCell align="right">{formatNumber(row.Arrears)}</TableCell>
                    <TableCell align="right">
                      {formatNumber(row.PAYEmanual)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ fontWeight: "bold", backgroundColor: "#e3f2fd" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Totals</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {formatNumber(totals.TaxablePay)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {formatNumber(totals.Pension)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {formatNumber(totals.PAYEauto)}
                  </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  {formatNumber(totals.NHIFauto)}
                </TableCell>

                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  {formatNumber(totals.AHLauto)}
                </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {formatNumber(totals.UnusedMPR)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {formatNumber(totals.MPRvalue)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {formatNumber(totals.Arrears)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {formatNumber(totals.PAYEmanual)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
           <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2
              }}
            >
              <Typography variant="body2" color="textSecondary">
                Printed at: {new Date().toLocaleString()}
              </Typography>
            </Box>

          {/* Buttons at bottom */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
              mb: 5,
            }}
          >
            <Button
              startIcon={<Print />}
              variant="contained"
              onClick={() => window.print()}
            >
              Print
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
            >
              Fetch Again
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
